import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";

export const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#e8ece6] via-stone-50 to-[#e8ece6]/50" />
      
      {/* Organic shapes */}
      <div className="absolute top-20 right-0 w-[600px] h-[600px] opacity-30">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/387b9bb4-7b54-4da0-9834-0f634fe26785/images/6aa70a0198cf633c789eb88aaeecc555777e2f05a8d037b4503232c59720c221.png"
          alt=""
          className="w-full h-full object-contain"
        />
      </div>
      
      {/* Decorative circles */}
      <div className="absolute top-40 left-10 w-32 h-32 rounded-full bg-[#8a9a86]/10 blur-2xl" />
      <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-[#2c4c3b]/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="max-w-3xl">
          {/* Label */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2c4c3b]/5 border border-[#2c4c3b]/10 mb-8 animate-fade-in-up">
            <Sparkles className="w-4 h-4 text-[#2c4c3b]" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.15em] uppercase text-[#2c4c3b] font-medium">
              {t('hero.badge')}
            </span>
          </div>

          {/* Headline */}
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-stone-900 tracking-tight leading-[1.1] mb-6 animate-fade-in-up animation-delay-100"
            data-testid="hero-title"
          >
            {t('hero.title1')}{" "}
            <span className="text-[#2c4c3b]">{t('hero.titleHighlight')}</span>
            <br />
            {t('hero.title2')}
          </h1>

          {/* Subheadline */}
          <p
            className="text-base md:text-lg text-stone-600 leading-relaxed max-w-xl mb-10 animate-fade-in-up animation-delay-200"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
            <Link to="/pricing" data-testid="hero-cta-scopri">
              <Button
                size="lg"
                className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-8 py-6 text-base transition-all hover:-translate-y-0.5 hover:shadow-xl group"
              >
                {t('hero.cta1')}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="#storia" data-testid="hero-cta-storia">
              <Button
                size="lg"
                variant="outline"
                className="border-[#2c4c3b]/20 text-[#2c4c3b] hover:bg-[#2c4c3b]/5 rounded-full px-8 py-6 text-base"
              >
                {t('hero.cta2')}
              </Button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="mt-16 flex items-center gap-8 animate-fade-in-up animation-delay-400">
            <div className="text-center">
              <p className="font-serif text-3xl text-[#2c4c3b]">30+</p>
              <p className="text-xs text-stone-500 uppercase tracking-wider mt-1">{t('hero.stat1')}</p>
            </div>
            <div className="w-px h-12 bg-stone-200" />
            <div className="text-center">
              <p className="font-serif text-3xl text-[#2c4c3b]">1000+</p>
              <p className="text-xs text-stone-500 uppercase tracking-wider mt-1">{t('hero.stat2')}</p>
            </div>
            <div className="w-px h-12 bg-stone-200" />
            <div className="text-center">
              <p className="font-serif text-3xl text-[#2c4c3b]">100%</p>
              <p className="text-xs text-stone-500 uppercase tracking-wider mt-1">{t('hero.stat3')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
